import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { portfolioProjects } from '../data/mockData';
import { X, ArrowRight, Instagram } from 'lucide-react';
import { useLang } from '../context/LanguageContext';

const tt = {
  fr: {
    label: 'Portfolio',
    h2a: 'Nos créations,',
    h2b: 'vos histoires',
    seoDesc: 'Découvrez nos réalisations en création de contenu photo et vidéo pour des restaurants, hôtels et lieux de vie à Strasbourg et ses environs.',
    all: 'Tous',
    menuLink: 'Voir le menu des offres',
    contactLink: 'Discuter de votre projet',
    context: 'Contexte',
    challenge: 'Enjeu',
    approach: 'Notre Approche',
    result: 'Résultat',
    services: 'Services',
    beforeAfterNote: 'Découvrez l\'évolution des grilles Instagram de nos clients, avant et après notre collaboration.',
    before: 'Avant',
    after: 'Après',
  },
  en: {
    label: 'Portfolio',
    h2a: 'Our creations,',
    h2b: 'your stories',
    seoDesc: 'Discover our photo and video content work for restaurants, hotels and hospitality venues in Strasbourg and surroundings.',
    all: 'All',
    menuLink: 'See our service menu',
    contactLink: 'Discuss your project',
    context: 'Context',
    challenge: 'Challenge',
    approach: 'Our Approach',
    result: 'Result',
    services: 'Services',
    beforeAfterNote: 'Discover the evolution of our clients\' Instagram grids, before and after our collaboration.',
    before: 'Before',
    after: 'After',
  },
};

const categories = ['all', 'Brand Design', 'Content Creation', 'Photography'];

/* Slideshow for "All" view cards */
const ProjectSlideshow = ({ images, title, className }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  useEffect(() => {
    if (!images || images.length <= 1) return;
    const interval = setInterval(() => setCurrentIndex((prev) => (prev + 1) % images.length), 4000);
    return () => clearInterval(interval);
  }, [images]);
  if (!images || images.length === 0) return null;
  return (
    <div className={`relative ${className}`}>
      <AnimatePresence mode="wait">
        <motion.img key={currentIndex} src={images[currentIndex]} alt={`${title} - ${currentIndex + 1}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.8 }} className="w-full h-full object-cover" />
      </AnimatePresence>
      {images.length > 1 && (
        <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex gap-2">
          {images.map((_, idx) => (<div key={idx} className={`w-2 h-2 rounded-full transition-all ${idx === currentIndex ? 'bg-white scale-125' : 'bg-white/50'}`} />))}
        </div>
      )}
    </div>
  );
};

/* ============ "ALL" VIEW — project cards with slideshow ============ */
const AllView = ({ projects, lang, getField, onSelectProject }) => (
  <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
    <AnimatePresence mode="popLayout">
      {projects.map((project, index) => (
        <motion.div key={project.id} layout initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-50px" }} exit={{ opacity: 0, scale: 0.9 }} transition={{ duration: 0.6, delay: index * 0.1 }} whileHover={{ y: -12, transition: { duration: 0.3 } }} onClick={() => onSelectProject(project)} data-testid={`portfolio-card-${project.id}`} className="group cursor-pointer rounded-2xl overflow-hidden relative" style={{ backgroundColor: project.bgColor }}>
          <div className="aspect-[4/3] overflow-hidden">
            {project.type === 'slideshow' && project.images ? (
              <div className="w-full h-full group-hover:scale-110 transition-transform duration-700">
                <ProjectSlideshow images={project.images} title={project.title} className="w-full h-full" />
              </div>
            ) : (
              <img src={project.image} alt={project.imageAlt || project.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
            )}
          </div>
          <div className="p-6 space-y-2">
            <h3 className="text-xl md:text-2xl font-semibold group-hover:underline" style={{ color: project.textColor }}>{project.title}</h3>
            <p className="text-base opacity-70" style={{ color: project.textColor }}>{getField(project.description)}</p>
            {project.collaboration && <p className="text-sm italic opacity-60" style={{ color: project.textColor }}>{getField(project.collaboration)}</p>}
          </div>
        </motion.div>
      ))}
    </AnimatePresence>
  </motion.div>
);

/* ============ BRAND DESIGN / CONTENT CREATION — grid screenshots with before/after ============ */
const GridView = ({ projects, lang, getField, tx }) => (
  <div className="space-y-20 max-w-6xl mx-auto">
    {/* Top note */}
    <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center text-lg md:text-xl text-[#151515]/70 italic max-w-3xl mx-auto">
      {tx.beforeAfterNote}
    </motion.p>

    {projects.filter(p => p.gridImage).map((project, index) => (
      <motion.div key={project.id} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: index * 0.15 }} className="space-y-6">
        <div className="text-center space-y-1">
          <h3 className="text-2xl md:text-3xl font-semibold text-[#151515]">{project.title}</h3>
          <p className="text-base text-[#151515]/60">{getField(project.description)}</p>
        </div>
        {project.gridIntro && (
          <p className="text-base md:text-lg text-[#151515]/70 text-center max-w-3xl mx-auto whitespace-pre-line leading-relaxed">{getField(project.gridIntro)}</p>
        )}

        {project.gridImageBefore ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <span className="block text-center text-sm uppercase tracking-widest font-mono text-[#151515]/40">{tx.before}</span>
              <div className="rounded-2xl overflow-hidden shadow-lg border border-[#151515]/5 opacity-75">
                <img src={project.gridImageBefore} alt={`${project.title} - ${tx.before}`} className="w-full h-auto" loading="lazy" />
              </div>
            </div>
            <div className="space-y-3">
              <span className="block text-center text-sm uppercase tracking-widest font-mono text-[#d4a574]">{tx.after}</span>
              <div className="rounded-2xl overflow-hidden shadow-lg border border-[#d4a574]/20">
                <img src={project.gridImage} alt={`${project.title} - ${tx.after}`} className="w-full h-auto" loading="lazy" />
              </div>
            </div>
          </div>
        ) : (
          <div className="rounded-2xl overflow-hidden shadow-lg border border-[#151515]/5">
            <img src={project.gridImage} alt={`${project.title} - Instagram grid`} className="w-full h-auto" loading="lazy" />
          </div>
        )}

        {project.photoCredit && (
          <p className="text-sm text-[#151515]/50 italic text-center">{getField(project.photoCredit)}</p>
        )}
      </motion.div>
    ))}
  </div>
);

/* ============ PHOTOGRAPHY — gallery of all photos per project ============ */
const PhotoGalleryView = ({ projects, lang, getField }) => (
  <div className="space-y-20 max-w-7xl mx-auto">
    {projects.filter(p => p.photos && p.photos.length > 0).map((project, index) => (
      <motion.div key={project.id} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: index * 0.15 }} className="space-y-6">
        <div className="text-center space-y-1">
          <h3 className="text-2xl md:text-3xl font-semibold text-[#151515]">{project.title}</h3>
          <p className="text-base text-[#151515]/60">{getField(project.description)}</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
          {project.photos.map((photo, idx) => (
            <motion.div key={idx} initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: idx * 0.05 }} className="aspect-square rounded-lg overflow-hidden group">
              <img src={photo} alt={`${project.title} - photo ${idx + 1}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
            </motion.div>
          ))}
        </div>
      </motion.div>
    ))}
  </div>
);

const PortfolioSection = () => {
  const { lang } = useLang();
  const tx = tt[lang];
  const [selectedProject, setSelectedProject] = useState(null);
  const [filter, setFilter] = useState('all');

  const getField = (field) => {
    if (!field) return '';
    return typeof field === 'object' ? (field[lang] || field.fr) : field;
  };

  return (
    <section id="portfolio" data-testid="portfolio-section" className="relative py-24 md:py-32 bg-white">
      <div className="container mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-5xl mx-auto mb-16">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="mb-6">
            <span className="text-sm uppercase tracking-widest text-[#717171] font-mono">{tx.label}</span>
          </motion.div>
          <motion.h2 initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }} className="text-4xl md:text-5xl lg:text-6xl font-semibold text-[#151515] mb-6 leading-tight">
            {tx.h2a}<br /><span className="text-[#4a5d6c]">{tx.h2b}</span>
          </motion.h2>
          <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.3 }} className="text-lg text-[#151515]/70 mb-8 max-w-2xl">{tx.seoDesc}</motion.p>

          {/* Category Filters */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.4 }} className="flex flex-wrap gap-3">
            {categories.map((cat) => (
              <button key={cat} onClick={() => setFilter(cat)} data-testid={`portfolio-filter-${cat.toLowerCase().replace(/\s+/g, '-')}`} className={`px-5 py-2 rounded-full text-sm uppercase tracking-wider font-mono transition-all ${filter === cat ? 'bg-[#151515] text-white' : 'bg-[#f5f5f0] text-[#151515] hover:bg-[#151515]/10'}`}>
                {cat === 'all' ? tx.all : cat}
              </button>
            ))}
          </motion.div>
        </div>

        {/* Content based on filter */}
        {filter === 'all' && (
          <AllView projects={portfolioProjects} lang={lang} getField={getField} onSelectProject={setSelectedProject} />
        )}
        {(filter === 'Brand Design' || filter === 'Content Creation') && (
          <GridView projects={portfolioProjects} lang={lang} getField={getField} tx={tx} />
        )}
        {filter === 'Photography' && (
          <PhotoGalleryView projects={portfolioProjects} lang={lang} getField={getField} />
        )}

        {/* Bottom links */}
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="max-w-5xl mx-auto mt-12 flex flex-wrap justify-center gap-6">
          <Link to="/menu" className="inline-flex items-center gap-2 text-[#1a3a52] hover:text-[#d4a574] transition-colors font-medium">{tx.menuLink} <ArrowRight size={18} /></Link>
          <a href="#contact" className="inline-flex items-center gap-2 text-[#1a3a52] hover:text-[#d4a574] transition-colors font-medium">{tx.contactLink} <ArrowRight size={18} /></a>
        </motion.div>
      </div>

      {/* Project Modal (only from "All" view) */}
      <AnimatePresence>
        {selectedProject && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setSelectedProject(null)} className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
            <motion.div initial={{ scale: 0.9, y: 50 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 50 }} onClick={(e) => e.stopPropagation()} data-testid="portfolio-modal" className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto relative">
              <button onClick={() => setSelectedProject(null)} data-testid="portfolio-modal-close" className="absolute top-6 right-6 z-10 w-10 h-10 rounded-full bg-black/10 hover:bg-black/20 flex items-center justify-center transition-colors"><X className="text-[#151515]" size={24} /></button>
              <div className="aspect-[16/9] overflow-hidden rounded-t-3xl">
                {selectedProject.type === 'slideshow' && selectedProject.images ? (
                  <ProjectSlideshow images={selectedProject.images} title={selectedProject.title} className="w-full h-full" />
                ) : (
                  <img src={selectedProject.image} alt={selectedProject.title} className="w-full h-full object-cover" />
                )}
              </div>
              <div className="p-8 md:p-12 space-y-6">
                {selectedProject.collaboration && <div className="inline-block px-4 py-2 rounded-full bg-[#d4a574]/20 text-[#d4a574] text-sm font-mono uppercase tracking-wider">{getField(selectedProject.collaboration)}</div>}
                <h2 className="text-3xl md:text-4xl font-bold text-[#151515]">{selectedProject.title}</h2>
                <p className="text-xl text-[#151515]/70">{getField(selectedProject.description)}</p>
                {selectedProject.photoCredit && (
                  <p className="text-sm text-[#151515]/50 italic">{getField(selectedProject.photoCredit)}</p>
                )}
                <div className="space-y-6 pt-4">
                  <div><h4 className="text-sm uppercase tracking-widest text-[#717171] font-mono mb-2">{tx.context}</h4><p className="text-lg text-[#151515]/80">{getField(selectedProject.context)}</p></div>
                  {selectedProject.challenge && <div><h4 className="text-sm uppercase tracking-widest text-[#717171] font-mono mb-2">{tx.challenge}</h4><p className="text-lg text-[#151515]/80">{getField(selectedProject.challenge)}</p></div>}
                  {selectedProject.approach && <div><h4 className="text-sm uppercase tracking-widest text-[#717171] font-mono mb-2">{tx.approach}</h4><p className="text-lg text-[#151515]/80">{getField(selectedProject.approach)}</p></div>}
                  {selectedProject.result && <div><h4 className="text-sm uppercase tracking-widest text-[#d4a574] font-mono mb-2">{tx.result}</h4><p className="text-lg text-[#151515]/80">{getField(selectedProject.result)}</p></div>}
                  {selectedProject.services && selectedProject.services.length > 0 && (
                    <div className="pt-4 border-t border-[#151515]/10">
                      <h4 className="text-sm uppercase tracking-widest text-[#717171] font-mono mb-3">{tx.services}</h4>
                      <div className="flex flex-wrap gap-2">{selectedProject.services.map((service, idx) => (<span key={idx} className="px-4 py-2 rounded-full text-sm bg-[#f5f5f0] text-[#151515]">{service}</span>))}</div>
                    </div>
                  )}
                  {selectedProject.instagramCta && (
                    <div className="pt-6 border-t border-[#151515]/10 text-center">
                      <p className="text-lg text-[#151515]/70 italic mb-3">{getField(selectedProject.instagramCta)}</p>
                      <a href={selectedProject.instagramCta.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#833AB4] via-[#C13584] to-[#E1306C] text-white rounded-full text-sm font-medium hover:opacity-90 transition-opacity" data-testid="instagram-cta-btn">
                        <Instagram size={18} /> Instagram
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default PortfolioSection;
